import '../../../css/discord.scss'
import '../../../css/leaderboard.scss'
import {EntryHomepage, uiManager} from '../../EntryHomepage'
import {UIDLoadingScreen} from './components/UIDLoadingScreen'
import {DiscordInstance} from '../DiscordInstance'
import {DiscordContentFrameController} from './DiscordContentFrameController'
import {SDKRequest} from '../../../../../shared/DiscordActivityTypes'
import {UISingletonScrollController} from '../../../../shared/scripts/ui_scroll_controller/UISingletonScrollController'
import {Scene} from '../../ui_html/UISceneTypes'
import {UIPopupManager} from '../../../../shared/scripts/ui_popup/UIPopupManager'
import {getValidSubPath, isValidPath, SceneSubPathType, stripProxyPrefix} from '../utils/ValidLinks'
import {PlayerEnums} from '../../../../../shared/GameEnums'
import {UIUrlController, UIUrlParameterNames} from '../../../../shared/scripts/ui_html/UIUrlController'
import {StorageWrapper} from '../../../../shared/scripts/StorageWrapper'
import {UIPopupController} from '../../../../shared/scripts/ui_popup/UIPopupController'
import {UIDConsoleLog} from './components/UIDConsoleLog'
import {LobbyImage} from '@colonist/types'
import {getHomepageUrlWithParameter, navigateToPage} from '../../../../shared/scripts/ui_utils/ClientUtils'
import {UIEntryUrlParameterController} from '../../../../shared/scripts/UIEntryUrlParameterController'
import {UIGameEndShareButtonController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndShareButtonController.ts'
import {UIWGeneralServicesController} from '../../UIWGeneralServicesController.ts'
import {SocketWebActionController} from '../../SocketActionController.ts'
import {SocketDiscordActionController} from './SocketDiscordActionController.ts'
import {socket, uiGameManager} from '../../../../shared/scripts/EntryShared.ts'
import {registerDiscordRoomClientHandlers} from './DiscordRoomClientHandlers.ts'
import {ClientUserState} from '../../../../shared/scripts/ClientUserState.ts'
import {UIAjaxWrapper} from '../../../../shared/scripts/network/UIAjaxWrapper.ts'
import {UIAPIEndpoints} from '../../../../shared/scripts/network/UIAPIEndpoints.ts'
import {ShareActivityLinkAnalytics} from '../../../../../shared/entity_datas/EntityDatas.ts'
import {ViewPopupPushReferrersToInviteMoreFriends} from '../../ui_popup/ViewPopupPushReferrersToInviteMoreFriends.ts'
import {SDKRequestHandler} from './sdk_requests/SDKRequestHandler.ts'
import {UIGameEndFooterController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndFooterController.ts'
import {MatchmakingMatchTypes, MatchType} from '@colonist/game-base-types'

const PUSH_REFERRERS_TO_INVITE_MORE_PLAYERS_MINIMUM_REFERRAL_COUNT = 1
const PUSH_REFERRERS_TO_INVITE_MORE_PLAYERS_MAXIMUM_REFERRAL_COUNT = 4

export class EntryDiscordContentFrame extends EntryHomepage {
    private discordInstance: DiscordContentFrameController
    private discordScenes: Map<string, [Scene, UIUrlParameterNames]>
    private removeChangeListener: () => void

    constructor() {
        super()
        void this.initialize()
    }

    async initialize() {
        new UIDConsoleLog()

        this.discordInstance = DiscordInstance.getDiscordInstance()
        await this.discordInstance.init(new UIDLoadingScreen())

        await super.initialize()

        registerDiscordRoomClientHandlers(socket.roomClient)

        this.registerDiscordScenes()
        EntryDiscordContentFrame.initDiscordControllers()

        this.overrideWindowOpen()

        EntryDiscordContentFrame.displayAccountLinkedPopup()
        this.setServerProfileOnHeader()

        document.addEventListener('click', (event) => this.interceptLinkClicks(event))

        this.setWebDiscordHandlers()

        void this.discordInstance.handlePendingPurchases()

        this.displayPushReferrersToInviteMoreFriendsPopup()
    }

    private displayPushReferrersToInviteMoreFriendsPopup() {
        this.removeChangeListener = ClientUserState.registerChangeListener(() => {
            // Defer execution to next tick to ensure assignment to this.removeChangeListener is complete
            setTimeout(async() => {
                if(ClientUserState.haveAbTestsLoaded == false) return

                this.removeChangeListener()

                if(EntryDiscordContentFrame.shouldShowInviteFriendsPopup()) {
                    const data = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiGetUserReferralCount(), undefined)
                    const referralCount = data?.json?.referralCount ?? 0

                    if(!EntryDiscordContentFrame.isReferralCountInRange(referralCount)) return

                    new ViewPopupPushReferrersToInviteMoreFriends(referralCount)

                    SDKRequestHandler.sendShareActivityLinkAnalytic(ShareActivityLinkAnalytics.ABTestPushReferrersToInviteMorePlayersGroupCPrePopupTriggered)

                    StorageWrapper.setLastPushReferrersToInviteMoreFriendsPopupTime(new Date())
                }
            }, 0)
        })
    }

    private static shouldShowInviteFriendsPopup(): boolean {
        const lastPushReferrersToInviteMoreFriendsPopupTime = StorageWrapper.getLastPushReferrersToInviteMoreFriendsPopupTime()
        if(UIGameEndFooterController.popupWasShownInLastTwentyFourHours(lastPushReferrersToInviteMoreFriendsPopupTime)) return false
        return true
    }

    private static isReferralCountInRange(referralCount: number): boolean {
        if(referralCount < PUSH_REFERRERS_TO_INVITE_MORE_PLAYERS_MINIMUM_REFERRAL_COUNT) return false
        if(referralCount > PUSH_REFERRERS_TO_INVITE_MORE_PLAYERS_MAXIMUM_REFERRAL_COUNT) return false
        return true
    }

    private registerDiscordScenes() {
        this.discordScenes = new Map<string, [Scene, UIUrlParameterNames]>()

        this.discordScenes.set('/leaderboard', [Scene.DiscordLeaderboard, UIUrlParameterNames.DiscordLeaderboard])
        this.discordScenes.set('/store', [Scene.DiscordStore, UIUrlParameterNames.DiscordStore])
        this.discordScenes.set('/about', [Scene.DiscordAbout, UIUrlParameterNames.DiscordAbout])
        this.discordScenes.set('/careers', [Scene.DiscordCareers, UIUrlParameterNames.DiscordCareers])
        this.discordScenes.set('/championship2025', [Scene.DiscordChampionship25, UIUrlParameterNames.DiscordChampionship25])
        this.discordScenes.set('/community', [Scene.DiscordCommunity, UIUrlParameterNames.DiscordCommunity])
        this.discordScenes.set('/faq', [Scene.DiscordFAQ, UIUrlParameterNames.DiscordFAQ])
        this.discordScenes.set('/privacy-policy', [Scene.DiscordPrivacyPolicy, UIUrlParameterNames.DiscordPrivacyPolicy])
        this.discordScenes.set('/patch-notes', [Scene.DiscordPatchNotes, UIUrlParameterNames.DiscordPatchNotes])
        this.discordScenes.set('/values', [Scene.DiscordValues, UIUrlParameterNames.DiscordValues])
        this.discordScenes.set('/terms', [Scene.DiscordTerms, UIUrlParameterNames.DiscordTerms])
        this.discordScenes.set('/press-kit', [Scene.DiscordPressKit, UIUrlParameterNames.DiscordPressKit])
        this.discordScenes.set('/refund-policy', [Scene.DiscordRefundPolicy, UIUrlParameterNames.DiscordRefundPolicy])
    }

    private static initDiscordControllers() {
        new UISingletonScrollController('scene_discord_store_container')
        new UISingletonScrollController('scene_discord_about_container')
        new UISingletonScrollController('scene_discord_community_container')
        new UISingletonScrollController('scene_discord_faq_container')
        new UISingletonScrollController('scene_discord_privacy_policy_container')
        new UISingletonScrollController('scene_discord_patch_notes_container')
        new UISingletonScrollController('scene_discord_values_container')
        new UISingletonScrollController('scene_discord_terms_container')
        new UISingletonScrollController('scene_discord_press_kit_container')
        new UISingletonScrollController('scene_discord_refund_policy_container')
    }

    private static displayAccountLinkedPopup() {
        const wasAccountLinked = StorageWrapper.getDiscordAccountLinked()
        if(!wasAccountLinked) return

        UIPopupController.createSimplePopup({key: 'strings:homePage.popups.discordAccountLinked.title'}, {key: 'strings:homePage.popups.discordAccountLinked.body'})
        StorageWrapper.removeDiscordAccountLinked()
    }

    private setServerProfileOnHeader() {
        const headerUsername = this.getUsernameForHeader()
        uiManager.headerProfile.discordUserStateOverrides = {
            username: headerUsername,
            profilePictureUrl: this.discordInstance.serverProfile.profilePictureUrl,
            icon: LobbyImage.Guest,
            karmaStatus: {key: 'strings:colonistCommon.utils.emptyString'},
        }
    }

    private interceptLinkClicks(event: MouseEvent) {
        const clickedElement = event.target as HTMLElement
        if(clickedElement == undefined) return

        const href = EntryDiscordContentFrame.getElementHref(clickedElement)
        if(!href) return

        event.preventDefault()

        this.handleLinkNavigation(href)
    }

    private overrideWindowOpen() {
        const originalWindowOpen = window.open

        window.open = (url?: string | URL, target?: string, features?: string) => {
            const urlString = url != undefined ? String(url) : ''
            if(this.handleLinkNavigation(urlString)) return null

            return originalWindowOpen.call(window, url, target, features)
        }
    }

    private handleLinkNavigation(link: string): boolean {
        if(link.startsWith('/#')) {
            const hash = link.substring(2)

            if(hash == `${UIUrlParameterNames.MainPage}=${UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE}`) {
                uiManager.sceneController.loadScene(Scene.MainPage)
                return true
            }
        }

        if(link.startsWith('#')) {
            const hash = link.substring(1)

            if(hash == 'profile') {
                const userState = ClientUserState.userState
                if(userState?.isLoggedIn && userState.username) {
                    uiManager.sceneController.loadScene(Scene.DiscordProfile, {username: userState.username})
                }
                return true
            }

            if(hash) {
                const elementToScrollTo = document.getElementById(hash)
                elementToScrollTo?.scrollIntoView({behavior: 'smooth'})
            }
            return true
        }

        if(link.includes('#matchmakingqueue=16')) {
            uiManager.header.notificationPanelController.hideNotificationPanel()
            void uiManager.goToMainPageAndSelectMatchType(MatchmakingMatchTypes.UnrankedShuffle)
            return true
        }

        if(link.includes(`#selectmode=`)) {
            const matchType = link.split('=')[1] as MatchType
            void uiManager.goToMainPageAndSelectMatchType(matchType)
            return true
        }

        const isExternalLink = ['https://', 'http://localhost:8080', 'mailto:'].some((prefix) => link.startsWith(prefix))
        if(isExternalLink) {
            this.discordInstance.sendSDKRequest(SDKRequest.OpenExternalLink, link)
            return true
        }

        const subPath = getValidSubPath(link)
        if(subPath != undefined) {
            switch(subPath.name) {
                case SceneSubPathType.Profile:
                    const profileData = subPath.extractData(link)
                    if(profileData == undefined) return false

                    const username = profileData[0]
                    uiManager.sceneController.scene == Scene.Room
                        ? uiManager.room.leaveRoom(Scene.DiscordProfile, {username: username})
                        : uiManager.sceneController.loadScene(Scene.DiscordProfile, {username: username})
                    return true
                case SceneSubPathType.Replay:
                    const replayData = subPath.extractData(link)
                    if(replayData == undefined) return false

                    const gameId = replayData[0]
                    const playerColor = replayData[1]
                    uiManager.sceneController.loadScene(Scene.DiscordReplay, {gameId: gameId, playerColor: playerColor})
                    return true
                case SceneSubPathType.GiftUser:
                    const giftData = subPath.extractData(link)
                    if(giftData == undefined) return false

                    const recipientUsername = giftData[0]
                    uiManager.sceneController.scene == Scene.Room
                        ? uiManager.room.leaveRoom(Scene.DiscordGifting, {recipientUsername: recipientUsername})
                        : uiManager.sceneController.loadScene(Scene.DiscordGifting, {recipientUsername: recipientUsername})
                    return true
                default: return false
            }
        }

        const scene = this.getDiscordScene(link)
        if(scene != undefined) {
            const {hash, value} = UIUrlController.extractHashAndValue(link)
            this.loadDiscordScene(scene, hash, value)
            if(hash) {
                const elementToScrollTo = document.getElementById(hash)
                elementToScrollTo?.scrollIntoView({behavior: 'smooth'})
            }
            return true
        }

        if(isValidPath(link)) {
            const strippedLink = stripProxyPrefix(link)
            const fullUrl = 'https://colonist.io' + strippedLink
            this.discordInstance.sendSDKRequest(SDKRequest.OpenExternalLink, fullUrl)
            return true
        }

        return false
    }

    static getElementHref(clickedElement: HTMLElement): string | null | undefined {
        const element = clickedElement.tagName
        const isATag = element == 'A'
        if(isATag) return clickedElement.getAttribute('href')

        if(clickedElement.getAttribute('id') == 'root-href') return '/' // TODO remove these where window.open is used
        if(clickedElement.getAttribute('id') == 'store-href') return '/store'

        if(clickedElement.classList.contains('deep-link')) {
            const maxDOMDepth = 5
            for(let i = 0; i < maxDOMDepth; i++) {
                if(clickedElement.tagName == 'A' && clickedElement.getAttribute('href')) return clickedElement.getAttribute('href')
                if(clickedElement.parentElement == null) break
                clickedElement = clickedElement.parentElement
            }
            return null
        }

        return clickedElement.parentElement?.getAttribute('href')
    }

    private getUsernameForHeader(): string {
        const username = this.discordInstance.serverProfile.username
        const isUsernameTooLong = username.length > PlayerEnums.PlayerConstants.MAX_USERNAME_LENGTH
        return isUsernameTooLong ? username.substring(0, PlayerEnums.PlayerConstants.MAX_USERNAME_LENGTH) : username
    }

    private getDiscordScene(href: string): string | undefined {
        for(const [path, [scene, urlParameter]] of this.discordScenes) {
            if(href.includes(path)) return path
        }
        return undefined
    }

    private loadDiscordScene(path: string, hash?: string, value?: string): boolean {
        const [scene, urlParameter] = this.discordScenes.get(path) as [Scene, UIUrlParameterNames]

        UIPopupManager.closeAllPopups()
        uiManager.header.closeMenu()
        uiManager.header.closeSidebarAndMenu()

        if(uiManager.sceneController.scene == Scene.Room) {
            uiManager.room.leaveRoom(scene)
            return true
        }

        if(uiManager.sceneController.scene == Scene.Game) {
            navigateToPage(getHomepageUrlWithParameter(urlParameter, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
            return true
        }

        uiManager.sceneController.loadScene(scene, {hash: hash, value: value})
        return true
    }

    private setWebDiscordHandlers() {
        UIGameEndShareButtonController.shareImageDiscord = (imageFile) => {
            void this.discordInstance.sendSDKRequest(SDKRequest.ShareMoment, imageFile)
        }
    }

    // eslint-disable-next-line class-methods-use-this
    protected createSocketActionController(generalServicesController: UIWGeneralServicesController): SocketWebActionController {
        return new SocketDiscordActionController(uiGameManager, uiManager, generalServicesController)
    }
}

new EntryDiscordContentFrame()
